import { breadcrumbs } from 'constants/breadcrumbs'
import { UserRoles } from 'constants/roles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { userRoleSelector } from 'redux/login/selectors'
import { MENU_NAMES } from 'texts/menuNames'
import { InfoIcon, MaximizeIcon, ModalCloseIcon } from 'ui/icons'
import { Tooltip, Button, Select, Input } from '@frontend/design_system'
import { OldEditableCell, Table } from 'ui/molecules/Table'
import { Confirmation } from 'ui/molecules/Confirmation'
import { Container } from 'ui/molecules/Container'
import { CANCEL, PAGINATION_DROPDOWN_LABEL, SAVE } from 'texts/uiTexts'
import { DEFAULT_INITIAL_PAGE } from 'constants/pagination'
import { VEHICLES, VEHICLES_DELETE_CONFIRMATION } from 'texts/vehicles'
import { Modal } from 'ui/molecules/Modal'
import { URLS } from 'constants/urls'
import { fleetApi } from 'api'
import {
  PriceListCar,
  PriceListRate,
  PriceListRateEdit,
  PriceListRates,
  CarClassItem,
} from 'api/fleet/types'
import { ItemType } from 'ui/atoms/Select/types'
import { useDispatch, useSelector } from 'react-redux'
import { RATES } from 'texts/rateDetails'
import { useApiRequest } from 'hooks/useApiRequest'
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode'
import { PRICE_LIST_DETAILS_BREADCRUMB } from 'texts/priceListDetails'
import { CarClassAddModal } from './components/CarClassAddModal'
import { setNotificationMessage } from 'redux/notifications/slice'
import { rateCodeWithSemicolonRegex } from 'constants/regex'
import { SUCCESS_UPDATE_MESSAGE } from 'texts/common'
import { ImportRate } from './components/ImportRate'
import { ExportRate } from './components/ExportRate'
import { TextField } from 'ui/atoms/TextField'
import { Typography } from 'ui/atoms/Typography'
import { formatSeasonRangeWithOneYear } from 'utils/seasonRanges'
import { getRatesHeadItems } from './utilts'
import { useCompanyId } from 'hooks/useCompanyId'
import { MAX_TEXT_FIELD_LENGTH } from './constants'
import { useCompanyInfo } from 'ui/hooks/useCompanyInfo'
import { useDidUpdate } from 'hooks/useDidUpdate'
import { UnsavedChangesModal } from 'ui/components/UnsavedChangesModal'
import { Pagination } from 'ui/molecules/Pagination'
import styles from './styles.module.scss'
import { usePagination } from 'hooks/usePagination'

export const Rates = () => {
  const params = useParams()
  const [availableCarClasses, setAvailableCarClasses] = useState<
    CarClassItem[]
  >([])
  const [rates, setRates] = useState<PriceListRates | null>(null)
  const isUpdatedRef = useRef(false)
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPaginationBlocked, setIsPaginationBlocked] = useState(false)
  const [pendingPage, setPendingPage] = useState<number | null>(null)
  const [pendingPageSize, setPendingPageSize] = useState<string | null>(null)
  const [currentSeasonRange, setCurrentSeasonRange] = useState({
    value: '',
    label: '',
  })
  const [tableContent, setTableContent] = useState<PriceListCar[]>([])
  const [currentRateCode, setCurrentRateCode] = useState('')
  const [selectedCarClass, setSelectedCarClass] = useState<string | null>(null)
  const [isRateCodeInvalid, setIsRateCodeInvalid] = useState(false)

  const { page, pageSize, handlePageSizeChange, handlePageChange } =
    usePagination()

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const priceListId = params.priceListId as string
  const role = useSelector(userRoleSelector)
  const companyId = useCompanyId()

  const isOperator = role === UserRoles.Operator
  const isReadOnlyMode = useReadOnlyMode(rates?.year)
  const hasSelectedRow = selectedCarClass !== null

  const { getCompanyInfo: getShortCompanyInfo } = useCompanyInfo(false)

  const priceListRatesResponse = useApiRequest((priceListId) =>
    fleetApi.getPriceListRates(priceListId, Number(pageSize), page - 1)
  )

  const deleteRateResponse = useApiRequest(
    (companyId, rateId) => fleetApi.deleteRate(companyId, rateId),
    undefined,
    undefined,
    undefined,
    false
  )

  const updateRatesResponse = useApiRequest(
    (companyId, priceListId, filteredRates) =>
      fleetApi.updatePriceListRate(priceListId, companyId, filteredRates),
    undefined,
    undefined,
    undefined,
    false
  )

  const addRateResponse = useApiRequest(
    (priceListId, vehiclesSipp) => fleetApi.addRate(priceListId, vehiclesSipp),
    undefined,
    undefined,
    undefined,
    false
  )

  const handleUpdate = () => {
    isUpdatedRef.current = true
  }

  const dailyRangeIds = useMemo(
    () => rates?.dailyRanges.map((range) => range.dailyRangeId) || [],
    [rates?.dailyRanges]
  )

  const currentSeasonRangeId = Number(currentSeasonRange.value)

  const filterContent = useCallback(
    (content: PriceListCar[]) => {
      if (!content.length) return []

      return content
        .map((car) => ({
          ...car,
          rates: car.rates
            .filter(
              (rate) =>
                rate.seasonRangeId === currentSeasonRangeId &&
                dailyRangeIds.includes(Number(rate.dailyRangeId))
            )
            .sort((a, b) => a.dailyRangeId - b.dailyRangeId),
        }))
        .filter((car) => car.rates.length > 0)
    },
    [currentSeasonRangeId, dailyRangeIds]
  )

  const breadcrumbList = useMemo(() => {
    const supplierBreadcrumbList = [
      {
        ...breadcrumbs.priceLists,
        link: `${breadcrumbs.priceLists.link}/${companyId}`,
      },
      {
        link: `${URLS.FLEET}${URLS.PRICE_LIST}/${companyId}/${priceListId}/edit`,
        label: PRICE_LIST_DETAILS_BREADCRUMB,
      },
    ]

    if (isOperator) {
      supplierBreadcrumbList.unshift({
        ...breadcrumbs.profileList,
        link: `${breadcrumbs.profileList.link}`,
      })
    }

    return supplierBreadcrumbList
  }, [priceListId, companyId, isOperator])

  const fetchRates = async (isInitial = false) => {
    const priceListRates = await priceListRatesResponse.apiRequest(priceListId)
    if (priceListRates) {
      setAvailableCarClasses(priceListRates.data.availableCarClasses)
      setRates(priceListRates.data)
      if (isInitial) {
        const currentSeasonRange =
          priceListRates.data.seasonRanges[0].description +
          priceListRates.data.year
        setCurrentSeasonRange({
          label: currentSeasonRange,
          value: String(priceListRates.data.seasonRanges[0].seasonRangeId),
        })
        setCurrentRateCode(priceListRates.data.seasonRanges[0].rateCode)
      }
    }
  }

  const onChangeRateCode = (value: string) => {
    setCurrentRateCode(value)
    setIsRateCodeInvalid(false)
    handleUpdate()
  }

  useEffect(() => {
    if (priceListId) fetchRates(true)
  }, [])

  useDidUpdate(() => {
    if (priceListId) fetchRates()
  }, [page, pageSize])

  useEffect(() => {
    if (rates) {
      setTableContent(filterContent(rates.cars.pageItems))
    }
  }, [currentSeasonRange, rates, filterContent])

  const handleChangeRange = (item: ItemType) => {
    setCurrentSeasonRange({
      value: item.value,
      label: item.label,
    })
    setCurrentRateCode(
      rates?.seasonRanges.find((el) => el.seasonRangeId === Number(item.value))
        ?.rateCode || ''
    )
    setTableContent(filterContent(tableContent))
  }

  const changePageSize = useCallback((value: string) => {
    if (isUpdatedRef.current) {
      setPendingPageSize(value)
      setPendingPage(DEFAULT_INITIAL_PAGE)
      setIsPaginationBlocked(true)
    }
  }, [])

  const handleChange = useCallback((pageNumber: number) => {
    if (isUpdatedRef.current) {
      setPendingPage(pageNumber)
      setPendingPageSize(null)
      setIsPaginationBlocked(true)
    }
  }, [])

  const handlePaginationProceed = () => {
    isUpdatedRef.current = false
    setIsPaginationBlocked(false)
    setPendingPage(null)
    setPendingPageSize(null)

    setTimeout(() => {
      if (pendingPage !== null) {
        handlePageChange(pendingPage)
      }
      if (pendingPageSize !== null) {
        handlePageSizeChange(pendingPageSize)
      }
    }, 0)
  }

  const handlePaginationReset = () => {
    setIsPaginationBlocked(false)
    setPendingPage(null)
    setPendingPageSize(null)
  }

  const mergeRates = useCallback(
    (tableContent: PriceListCar[], newRestRates?: PriceListCar[]) => {
      const ratesList = newRestRates || rates?.cars.pageItems
      if (!ratesList) return null

      const ratesFromUnselectedRanges = ratesList.map((car) =>
        car.rates.filter((rate) => rate.seasonRangeId !== currentSeasonRangeId)
      )

      return tableContent.map((priceListCar, i) => ({
        ...priceListCar,
        rates: [
          ...priceListCar.rates.map((rate) => ({
            ...rate,
            rateCode: currentRateCode,
          })),
          ...(ratesFromUnselectedRanges[i] || []),
        ],
      }))
    },
    [rates?.cars.pageItems, currentSeasonRangeId, currentRateCode]
  )

  const handleChangeValue = useCallback(
    (col: number, rowIndex: number, value: number) => {
      const allRates = mergeRates(tableContent)
      if (!allRates) return

      const newTableContent = [...tableContent]
      newTableContent[rowIndex].rates[col].price = value

      setTableContent(newTableContent)
    },
    [tableContent]
  )

  const formattedHeadItems = useMemo(() => {
    const items = getRatesHeadItems(rates?.priceListType).map((header) => ({
      value: header,
    }))
    rates?.dailyRanges?.forEach((el) => items.push({ value: el.description }))
    return items
  }, [rates])

  const handleModalClose = () => {
    setConfirmationModalOpen(false)
    setSelectedCarClass(null)
  }

  const getEditableRates = (rates: PriceListRate[], row: number) =>
    rates.map((el, col) => (
      <OldEditableCell
        key={Date.now() * el.price}
        handleUpdate={handleUpdate}
        onChange={(value) => handleChangeValue(col, row, Number(value))}
        initValue={el.price}
        readOnly={isReadOnlyMode}
      />
    ))

  const handleSave = async (
    tableContent: PriceListCar[],
    newRestRates?: PriceListCar[]
  ) => {
    const allRates = mergeRates(tableContent, newRestRates)
    if (!allRates) return

    const formattedRates: PriceListRateEdit[] = allRates.flatMap((car) =>
      car.rates.map((rate) => ({
        ...rate,
        carClass: car.carClass,
      }))
    )

    const response = await updateRatesResponse.apiRequest(
      companyId,
      priceListId,
      formattedRates
    )

    if (response) {
      setSelectedCarClass(null)
      setConfirmationModalOpen(false)
      isUpdatedRef.current = false
      dispatch(
        setNotificationMessage({
          notificationMessage: SUCCESS_UPDATE_MESSAGE,
        })
      )
      fetchRates()
      if (!isOperator) {
        getShortCompanyInfo(companyId)
      }
    }
  }

  const handleDeleteRange = (carClass: string) => {
    setConfirmationModalOpen(true)
    setSelectedCarClass(carClass)
  }

  const handleRemoveRow = async () => {
    if (hasSelectedRow) {
      const ratesIds = tableContent.reduce((acc, el) => {
        if (el.carClass === selectedCarClass) {
          el.rates.forEach((rate) => {
            acc.push(rate.rateId)
          })
        }
        return acc
      }, [] as number[])

      await deleteRateResponse.apiRequest(companyId, ratesIds)
      fetchRates()
      setConfirmationModalOpen(false)
      setSelectedCarClass(null)
    }
  }

  const formattedBodyItems = useMemo(
    () =>
      tableContent.map((el, row) => ({
        items: [el.carClass, el.carName, ...getEditableRates(el.rates, row)],
        removeRowClick: isReadOnlyMode
          ? undefined
          : () => (el.carClass ? handleDeleteRange(el.carClass) : undefined),
        reserveRemoveIconSpace: !isReadOnlyMode,
      })),
    [tableContent, isReadOnlyMode]
  )

  const handleCancel = () => {
    if (isModalOpen) {
      setIsModalOpen(false)
    } else {
      navigate(`${URLS.FLEET}${URLS.PRICE_LIST}/${companyId}`)
    }
  }

  const saveTableContent = async () => {
    if (currentRateCode && !rateCodeWithSemicolonRegex.test(currentRateCode)) {
      setIsRateCodeInvalid(true)
      return
    }
    handleSave(tableContent)
  }

  const handleAddRate = async (vehicleSipp: string[]) => {
    const response = await addRateResponse.apiRequest(priceListId, vehicleSipp)
    if (response) {
      fetchRates()
      if (!isOperator) {
        getShortCompanyInfo(companyId)
      }
    }
  }

  const renderTable = () => (
    <>
      <div className={styles.list}>
        <div className={styles.header}>
          <div className={styles.buttons}>
            <ImportRate
              priceListId={priceListId}
              updateRates={fetchRates}
              disabled={isReadOnlyMode}
            />
            <ExportRate
              priceListId={priceListId}
              disabled={!rates?.cars.pageItems.length || isReadOnlyMode}
              isUpdated={isUpdatedRef.current}
              saveTableContent={saveTableContent}
            />
            <div className={styles['car-class-wrapper']}>
              <CarClassAddModal
                carClasses={availableCarClasses}
                handleChangeContent={handleAddRate}
                disabled={isReadOnlyMode}
                loading={addRateResponse.loading}
              />
            </div>
            <div className={styles['filter-icon']}>
              {!isModalOpen ? (
                <MaximizeIcon
                  color="blue700"
                  size="large"
                  onClick={() => setIsModalOpen(true)}
                />
              ) : (
                <ModalCloseIcon
                  size="medium"
                  onClick={() => setIsModalOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
        {formattedBodyItems && (
          <div className={styles.table}>
            <Table
              headItems={formattedHeadItems}
              bodyItems={formattedBodyItems}
            />
          </div>
        )}
      </div>
      <footer className={styles.pagination}>
        <Pagination
          defaultPage={page}
          defaultPageSize={Number(pageSize)}
          pageItemCount={rates?.cars.pageItems.length}
          total={rates?.cars.totalItems || 0}
          onPageSizeChange={changePageSize}
          onPageChange={handleChange}
          pageSizeLabel={PAGINATION_DROPDOWN_LABEL}
        />
      </footer>
    </>
  )

  const renderTextField = (value = '', label: string) => (
    <div className={styles.wrapper}>
      <TextField
        label={label}
        value={
          <div className={styles['location-info']}>
            <Typography Tag="div" name="Button2" className={styles.location}>
              {value}
            </Typography>
            {value.length > MAX_TEXT_FIELD_LENGTH && (
              <Tooltip
                size="primary"
                arrowPosition="right"
                popupContent={value}
                placement="bottom"
              >
                <InfoIcon color="blue700" size="medium" />
              </Tooltip>
            )}
          </div>
        }
      />
    </div>
  )

  const selectedCarName = useMemo(
    () => tableContent.find((el) => el.carClass === selectedCarClass)?.carName,
    [tableContent, selectedCarClass]
  )

  return (
    <>
      {isModalOpen && (
        <Modal
          position="center-horizontal"
          onClose={() => setIsModalOpen(false)}
          offsetVariant="none"
          invisibleTitle
          invisibleHeader
          invisibleClose
          headerSize="large"
        >
          <div className={styles['rates-modal']}>{renderTable()}</div>
        </Modal>
      )}
      <Container
        breadcrumbList={breadcrumbList}
        currentPageLabel={MENU_NAMES.RATES}
        title={MENU_NAMES.RATES}
        withNavigation={isOperator}
        loading={priceListRatesResponse.loading}
        rightBlock={
          <div className={styles['right-side']}>
            <div className={styles['action-button']}>
              <Button
                label={CANCEL}
                variant="secondary-border"
                onClick={handleCancel}
              />
            </div>
            <div className={styles['action-button']}>
              <Button
                label={SAVE}
                disabled={isReadOnlyMode || !isUpdatedRef.current}
                onClick={saveTableContent}
                loading={updateRatesResponse.loading}
              />
            </div>
          </div>
        }
      >
        <>
          <div>
            <UnsavedChangesModal
              isDirty={isUpdatedRef.current}
              customBlockers={[
                {
                  isBlocked: isPaginationBlocked,
                  onProceed: handlePaginationProceed,
                  onReset: handlePaginationReset,
                },
              ]}
            />

            <div className={styles['rate-info-wrapper']}>
              <div className={styles['range-wrapper']}>
                {!rates?.seasonRanges || rates?.seasonRanges.length > 1 ? (
                  <Select
                    items={
                      rates?.seasonRanges?.map((el) => ({
                        label: formatSeasonRangeWithOneYear(
                          el.description,
                          rates.year
                        ),
                        value: String(el.seasonRangeId),
                      })) || []
                    }
                    label={RATES.SEASON_RANGES}
                    onChange={(_value: string, item: ItemType) =>
                      handleChangeRange(item)
                    }
                    selectedValue={currentSeasonRange.value}
                    size="large"
                  />
                ) : (
                  <div className={styles['single-range']}>
                    <Typography
                      Tag="div"
                      name="Button2"
                      className={styles['single-range-block-title']}
                    >
                      {RATES.SEASON_RANGES}
                    </Typography>
                    <Typography
                      Tag="div"
                      name="Button2"
                      className={styles['single-range-block-value']}
                    >
                      {formatSeasonRangeWithOneYear(
                        rates?.seasonRanges[0].description,
                        rates.year
                      )}
                    </Typography>
                  </div>
                )}
              </div>
              <div className={styles['input-wrapper']}>
                <Input
                  value={currentRateCode}
                  placeholder={RATES.RATE_CODE.PLACEHOLDER}
                  size="large"
                  error={
                    isRateCodeInvalid ? RATES.RATE_CODE.VALIDATION_MESSAGE : ''
                  }
                  onChange={onChangeRateCode}
                  spaceForError="none"
                  label={RATES.RATE_CODE.LABEL}
                  tooltip={RATES.RATE_CODE.TOOLTIP}
                />
              </div>
            </div>
            <div className={styles['rate-info']}>
              {renderTextField(rates?.country, RATES.COUNTRY)}
              {renderTextField(rates?.location, RATES.LOCATION)}
              {renderTextField(rates?.countriesOfResidence, RATES.COR)}
            </div>
          </div>
          {renderTable()}
        </>
      </Container>
      {isConfirmationModalOpen && hasSelectedRow && (
        <Confirmation
          confirmModalProps={{
            question: `${VEHICLES_DELETE_CONFIRMATION.CONFIRMATION_MODAL.QUESTION} ${selectedCarName}?`,
            onConfirm: handleRemoveRow,
            onClose: handleModalClose,
            title: `${VEHICLES.DELETE} ${selectedCarName} ${VEHICLES.MODEL}`,
            loading: deleteRateResponse.loading,
          }}
        />
      )}
    </>
  )
}
